<template>
  <div class="landing-page about-page">
    <header class="landing-header">
      <div class="header-inner">
        <div class="header-brand">
          <img :src="require('@/assets/image/common/tool-bar-logo.png')" alt="" class="header-logo-icon">
          <span class="header-logo-text"><span class="header-logo-bold">MYTEC</span><span class="header-logo-light"></span></span>
        </div>
        <nav class="header-nav">
          <a href="#hero" class="header-link" @click.prevent="scrollToSection('hero')">About</a>
          <span class="header-sep" aria-hidden="true" />
          <a href="#vision" class="header-link" @click.prevent="scrollToSection('vision')">AI Technology</a>
          <span class="header-sep" aria-hidden="true" />
          <a href="#news" class="header-link" @click.prevent="scrollToSection('news')">News</a>
          <span class="header-sep" aria-hidden="true" />
          <a href="#product" class="header-link" @click.prevent="scrollToSection('product')">Product</a>
          <span class="header-sep" aria-hidden="true" />
          <a href="#contact" class="header-link" @click.prevent="scrollToSection('contact')">Contact</a>
        </nav>
        <div class="header-cta">
          <button type="button" class="btn btn-cta-nav ripple-btn" @click="ripple($event); $router.push('/loginNew')">
            Start Free Trial
          </button>
        </div>
      </div>
    </header>

    <section id="hero" class="hero hero-about hero-about-with-bg">
      <div class="hero-bg-wrap">
        <img src="/static/about/hero-bg.jpg" :data-fallback="imageFallbacks.hero" alt="" class="hero-bg-img" @error="onImgError($event)" />
      </div>
      <p class="hero-pretitle">MYTEC Education</p>
      <h1 class="hero-title">AI-Powered Personalized Math Learning</h1>
      <p class="hero-subtitle">MYTutor helps students master mathematics through intelligent diagnostics, adaptive learning paths, and targeted practice.</p>
      <div class="hero-cta">
        <div @click="$router.push('/loginNew')" class="btn btn-hero-primary ripple-btn">
          Learn More <span class="hero-cta-arrow" aria-hidden="true">→</span>
        </div>
      </div>
    </section>

    <section id="vision" class="section">
      <span class="section-badge-pill">Our Vision</span>
      <h2 class="section-title">Empowering the future of learning with AI</h2>
      <p class="section-desc section-desc-wide">
        To revolutionize education by providing smart, scalable, and student-centric solutions that help learners achieve their full potential—regardless of background or learning style.
      </p>
      <div class="about-block about-mission">
        <p>At <strong>MYTEC Education Technology Inc.</strong>, we believe every student deserves a personalized learning experience tailored to their unique needs. As an innovative AI-driven edtech company, we specialize in developing intelligent, adaptive learning platforms and apps designed for the K-12 education market.</p>
        <p><strong>Our mission</strong> is to transform traditional education by harnessing the power of artificial intelligence, data analytics, and interactive technology—making learning more effective, engaging, and accessible for students worldwide.</p>
      </div>
    </section>

    <section id="product" class="section section-alt">
      <span class="section-badge-pill">Product</span>
      <h2 class="section-title">MYTutor Math</h2>
      <p class="section-desc">Smarter math learning powered by AI.</p>
      <p class="section-desc">MYTutor analyzes each student's strengths and weaknesses to build a personalized learning experience.</p>
      <div class="about-product-img-wrap">
        <img src="/static/about/product.jpg" :data-fallback="imageFallbacks.product" alt="MYTutor Math" class="about-product-img" @error="onImgError($event)" />
      </div>
      <div class="hero-cta">
        <router-link to="/landing" class="feature-link feature-link-btn ripple-btn">For more information <span aria-hidden="true">→</span></router-link>
      </div>
    </section>

    <section id="news" class="section">
      <span class="section-badge-pill">MYTec News</span>
      <h2 class="section-title">Latest Updates</h2>
      <div class="news-list">
        <article
          v-for="(item, i) in news"
          :key="i"
          class="news-card card-hover"
          @click="ripple($event); item.expanded = !item.expanded"
        >
          <div class="flex">
            <div class="left">
              <img :src="item.img" alt="" class="" />
            </div>
            <div class="right">
              <div class="news-card-header">
                <time class="news-date">{{ item.date }}</time>
                <h3 class="news-title">{{ item.title }}</h3>
              </div>
              <p class="news-excerpt" :class="{ expanded: item.expanded }">
                {{ item.expanded ? item.body : item.excerpt }}
                <button v-if="!item.expanded" type="button" class="news-more ripple-btn" @click.stop="item.expanded = true">Show More</button>
                <button v-else type="button" class="news-more ripple-btn" @click.stop="item.expanded = false">Show Less</button>
              </p>
            </div>
          </div>

        </article>
      </div>
    </section>

    <section id="team" class="section section-alt">
      <span class="section-badge-pill">Our Team</span>
      <h2 class="section-title">With passion for education</h2>
      <div class="team-grid">
        <div v-for="(member, i) in team" :key="i" class="team-card card-hover" @click="ripple($event)">
          <div class="team-avatar-wrap">
            <img
              v-if="member.img"
              :src="member.img"
              :data-fallback="member.imgFallback"
              :alt="member.name"
              class="team-avatar-img"
              @error="onTeamImgError($event, member)"
            />
            <div class="team-avatar" :class="{ 'team-avatar-visible': !member.img || member.avatarError }">{{ member.initials }}</div>
          </div>
          <h3 class="team-name">{{ member.name }}</h3>
          <p class="team-role">{{ member.role }}</p>
          <p class="team-title">{{ member.title }}</p>
        </div>
      </div>
    </section>

    <section id="contact" class="section section-get-started">
      <div class="get-started">
        <h2 class="get-started-title">Get Started Today</h2>
        <p class="get-started-desc">Contact us to learn more about how MYTutor Math can help your child excel in mathematics</p>
        <div class="get-started-grid">
          <form class="get-started-form" @submit.prevent="onSubmit">
            <div class="form-field">
              <label class="form-label">Full Name *</label>
              <input v-model="form.fullName" type="text" placeholder="Enter your name" required class="get-started-input ripple-input" @click="ripple($event)" />
            </div>
            <div class="form-field">
              <label class="form-label">Email Address *</label>
              <input v-model="form.email" type="email" placeholder="your.email@example.com" required class="get-started-input ripple-input" @click="ripple($event)" />
            </div>
            <div class="form-field">
              <label class="form-label">Phone Number</label>
              <input v-model="form.phone" type="tel" placeholder="(123) 456-7890" class="get-started-input ripple-input" @click="ripple($event)" />
            </div>
            <div class="form-field">
              <label class="form-label">Student Grade Level *</label>
              <select v-model="form.grade" required class="get-started-input get-started-select ripple-input" @click="ripple($event)">
                <option value="">Select grade level</option>
                <option value="k2">Kindergarten - 2nd Grade</option>
                <option value="35">3rd - 5th Grade</option>
                <option value="68">6th - 8th Grade</option>
                <option value="912">9th - 12th Grade</option>
              </select>
            </div>
            <div class="form-field">
              <label class="form-label">Message</label>
              <textarea v-model="form.message" placeholder="Tell us about your learning goals..." rows="4" maxlength="500" class="get-started-input get-started-textarea ripple-input" @click="ripple($event)" />
              <span class="get-started-char">{{ (form.message || '').length }}/500 characters</span>
            </div>
            <button type="submit" class="btn-get-started ripple-btn" @click="ripple($event)">
              Send Message
            </button>
          </form>
          <div class="get-started-info">
            <div class="info-block">
              <span class="info-icon info-icon-purple"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></span>
              <div class="info-content">
                <h3 class="info-heading">Visit Us</h3>
                <p class="info-text">1739 Southmere Crescent Surrey BC Canada V4A7A8</p>
              </div>
            </div>
            <div class="info-block">
              <span class="info-icon info-icon-teal"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></span>
              <div class="info-content">
                <h3 class="info-heading">Email Us</h3>
                <p class="info-text"><a href="mailto:info@mytec-edu.com" class="info-link ripple-btn" @click="ripple($event)">info@mytec-edu.com</a></p>
                <p class="info-sub">We'll respond within 24 hours.</p>
              </div>
            </div>
            <div class="info-block info-block-follow">
              <h3 class="info-heading">Follow Us</h3>
              <div class="info-social">
                <a href="#" class="info-social-icon ripple-btn" aria-label="Facebook" @click.prevent="ripple($event)"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg></a>
                <a href="#" class="info-social-icon ripple-btn" aria-label="Twitter" @click.prevent="ripple($event)"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg></a>
                <a href="#" class="info-social-icon ripple-btn" aria-label="Instagram" @click.prevent="ripple($event)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg></a>
                <a href="#" class="info-social-icon ripple-btn" aria-label="LinkedIn" @click.prevent="ripple($event)"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4V14a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg></a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 页脚：与参考图完全一致 - 三列 + 底栏 -->
    <footer class="landing-footer">
      <div class="footer-inner">
        <div class="footer-main">
          <div class="footer-col footer-col-brand">
            <div class="footer-logo">MYTutor <span class="footer-logo-accent">Math</span></div>
            <p class="footer-desc">Empowering K-12 students with AI-powered personalized math learning. Our platform adapts to each student's unique learning style and pace, making mathematics accessible and enjoyable for everyone.</p>
            <div class="footer-social">
              <a href="#" class="footer-social-icon" aria-label="Facebook" @click.prevent="ripple($event)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg></a>
              <a href="#" class="footer-social-icon" aria-label="Twitter" @click.prevent="ripple($event)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg></a>
              <a href="#" class="footer-social-icon" aria-label="Instagram" @click.prevent="ripple($event)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg></a>
              <a href="#" class="footer-social-icon" aria-label="YouTube" @click.prevent="ripple($event)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" fill="currentColor"/></svg></a>
            </div>
          </div>
          <div class="footer-col footer-col-contact">
            <h3 class="footer-label">CONTACT ADDRESS</h3>
            <p class="footer-contact-line">1739 Southmere Crescent Surrey</p>
            <p class="footer-contact-line">BC Canada V4A7A8</p>
            <h3 class="footer-label">EMAIL ADDRESS</h3>
            <p class="footer-contact-line"><a href="mailto:info@mytec-edu.com" class="footer-link ripple-btn" @click="ripple($event)">info@mytec-edu.com</a></p>
          </div>
          <div class="footer-col footer-col-img">
            <div class="footer-img-wrap">
              <img src="/static/landing/footer-student.jpg" alt="Student learning" class="footer-img" data-fallback="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80" @error="onImgError($event)" />
            </div>
          </div>
        </div>
        <div class="footer-bar">
          <span class="footer-copy">© 2026 MYTutor Math. All rights reserved.</span>
          <div class="footer-legal">
            <a href="#" class="footer-legal-link ripple-btn" @click.prevent="ripple($event)">Privacy Policy</a>
            <a href="#" class="footer-legal-link ripple-btn" @click.prevent="ripple($event)">Terms of Service</a>
            <a href="#" class="footer-legal-link ripple-btn" @click.prevent="ripple($event)">Help Center</a>
          </div>
        </div>
      </div>
    </footer>

    <span v-show="rippleVisible" class="ripple ripple-fixed" :style="rippleStyle" />
  </div>
</template>

<script>
export default {
  name: 'About',
  data () {
    return {
      form: {
        fullName: '',
        email: '',
        phone: '',
        grade: '',
        message: ''
      },
      rippleVisible: false,
      rippleStyle: {},
      _rippleTimer: null,
      imageFallbacks: {
        hero: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&q=80',
        product: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80',
        footer: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80'
      },
      team: [
        { name: 'Jim, ZHANG HONG', role: 'Founder', title: 'CEO of MYTEC Education', initials: 'JZ', img: '/static/about/team-1.jpg', imgFallback: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80', avatarError: false },
        { name: 'Sherry, ZHANG JIANXIONG', role: 'Co-Founder', title: 'Marketing Director of MYTEC Education', initials: 'SZ', img: '/static/about/team-2.jpg', imgFallback: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80', avatarError: false },
        { name: 'GU JING', role: 'Co-Founder', title: 'CTO of MYTEC Education', initials: 'GJ', img: '/static/about/team-3.jpg', imgFallback: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&q=80', avatarError: false }
      ],
      news: [
        {
          img: '/static/about/9.png',
          date: 'May 2026',
          title: 'MYTutor Math Officially Launches in May 2026, Entering B2B and B2C Market Testing Phase',
          excerpt: 'May 2026 — Vancouver, Canada: MYTEC officially launched MYTutor Math and moved from product development into live B2B and B2C market validation with initial customer and student onboarding.',
          body: 'May 2026 — Vancouver, Canada: MYTEC Education officially announced the launch of MYTutor Math, the company\'s AI-powered intelligent learning platform, marking an important milestone in the project\'s transition from product development to live market validation. Following multiple stages of technical development, AI integration, and internal testing, MYTutor Math is now entering its initial B2B and B2C market testing phase. The platform combines intelligent AI-assisted learning with personalized learning management, aiming to deliver a more adaptive and efficient learning experience for students. Since launch, MYTutor Math has already secured its first B2B customer order and successfully activated initial student accounts for operational testing and user feedback collection. The current testing phase focuses on real-world classroom and tutoring applications, student engagement and learning performance, AI-assisted personalization effectiveness, parent and educator feedback, and platform stability and scalability. As MYTutor Math moves into commercialization, MYTEC will continue to optimize AI learning interactions and recommendation systems, expand partnerships with tutoring centers and educational organizations, develop broader B2C user acquisition channels, and improve learning analytics and personalized learning pathways. The MYTEC team believes the future of education lies in combining artificial intelligence with structured personalized learning management, creating a truly intelligent learning ecosystem rather than a simple AI chat tool. More updates will follow as MYTutor Math continues expanding its market presence throughout 2026.',
          expanded: false
        },
        {
          img: '/static/about/1.png',
          date: 'February 10, 2026',
          title: 'MYTEC Founding Team Reunites in Vancouver, Entering a New Development Phase',
          excerpt: 'Vancouver, February 2026 — The MYTEC founding team gathered in Vancouver for the first time in 2026, reviewed product progress, and aligned on the next release roadmap for MYTutor.',
          body: 'Vancouver, February 2026 — The MYTEC founding team gathered in Vancouver for the first time in 2026, marking an important milestone in the company\'s growth and the continued advancement of the MYTutor intelligent learning platform. During the meeting, the team reviewed recent development progress, aligned on product optimization strategies, and confirmed the roadmap toward the next release phase. With AI integration, database architecture, and intelligent learning management systems steadily advancing, MYTutor is now entering a new stage of refinement and scalability. This reunion reflects MYTEC\'s strong commitment to building a truly intelligent and personalized learning platform, and more updates will follow as MYTutor moves closer to its next major milestone.',
          expanded: false
        },
        {
          img: '/static/about/2.png',
          date: 'January 22, 2026',
          title: 'Mytec CEO Visits St. John\'s Academy to Explore Educational Collaboration',
          excerpt: 'Victoria, BC — MYTEC visited St. John\'s Academy to discuss summer school, study tours, short-term international programs, and classroom AI applications for K-12 learning.',
          body: 'Victoria, BC — Mr. Jim Zhang, CEO of Mytec Education Inc., recently visited St. John\'s Academy, accompanied by Board Director Mr. Tom Guo. During the visit, both parties held in-depth discussions on summer school programs, educational study tours, short-term international study initiatives, and the application of AI in daily classroom teaching. Also attending were Mr. Eric Ma, Principal of UEC Academy, Ms. Sylvia Chen, General Manager of LUMEN Education, and Mr. Bee, Sales Manager of Mytec Education Inc. The delegation explored opportunities to integrate AI-driven educational solutions into K-12 education to enhance personalized learning and teaching efficiency. The visit marked a positive step toward future collaboration in academic programs and educational technology innovation.',
          expanded: false
        },
        {
          img: '/static/about/3.png',
          date: 'November 17, 2025',
          title: 'MYTEC EDUCATION TECHNOLOGY INC. Announces Preliminary Collaboration Intentions with St. John\'s Academy and IVY 100 EDUCATION',
          excerpt: 'Greater Vancouver, BC, Canada — MYTEC reached preliminary collaboration intentions with St. John\'s Academy and IVY 100 EDUCATION to deploy MyTutor Math AI through pilot programs.',
          body: 'Greater Vancouver, BC, Canada — MYTEC Education Technology Inc., an innovative education technology company in AI-powered educational solutions, is pleased to announce that it has reached preliminary agreements for collaboration with St. John\'s Academy, a renowned private school, and IVY 100 EDUCATION, a leading SAT training institution, regarding the deployment of its flagship educational application, MyTutor Math AI. MyTutor Math AI is an advanced learning platform that leverages artificial intelligence and adaptive learning technologies to deliver personalized mathematics instruction. Through these preliminary collaborations, MYTEC plans to introduce pilot programs, collect user feedback and learning analytics, and explore long-term integration opportunities for both classroom learning and SAT preparation.',
          expanded: false
        },
        {
          img: '/static/about/4.png',
          date: 'August 28, 2025',
          title: 'MYTEC Explores Collaboration with Royal Canadian Christian School on MYTutor Math AI',
          excerpt: 'Vancouver, BC — MYTEC and Royal Canadian Christian School discussed pilot collaboration around MYTutor Math AI to improve learning efficiency and outcomes for K-12 students.',
          body: 'Vancouver, BC — August 28, 2025 — MYTEC Education Inc. is advancing discussions on the future of artificial intelligence in K-12 education through a recent meeting with Royal Canadian Christian School. The focus of the discussion was MYTEC\'s innovative platform, MYTutor Math AI, and its potential to enhance student learning outcomes. The meeting was attended by Jim Zhang, CEO of MYTEC, Linda Lin, Consultant at MYTEC, and a representative of Royal Canadian Christian School. Both parties engaged in a comprehensive exchange of views on intelligent tutoring, real-time feedback, individualized study paths, pilot programs, and curriculum integration. MYTEC and Royal Canadian Christian School both expressed confidence in the collaboration and its long-term educational value.',
          expanded: false
        },
        {
          img: '/static/about/5.png',
          date: 'August 20, 2025',
          title: 'MYTEC Advances SUV Project with Key Development of MYTutor Math AI',
          excerpt: 'Vancouver, BC — MYTEC entered a pivotal stage in its SUV project as MYTutor AI completed phase one and moved into MYTutor Math AI design and development.',
          body: 'Vancouver, BC — August 20, 2025 — MYTEC Education Inc. has entered a pivotal stage in the development of its Startup Visa (SUV) project, marking significant progress in its flagship product, MYTutor AI. The company announced that the platform has successfully completed its initial phase and is now moving into the second stage with the design and development of MYTutor Math AI. In a recent meeting between MYTEC leadership and PacTech Incubator, both parties engaged in in-depth discussions regarding product development, market direction, and the Canadian Startup Visa program. MYTEC CEO Jim Zhang, consultants Linda and Peter, and PacTech’s Wade Laerence jointly recognized the strategic adjustment of MYTutor’s direction—from a single AI tool to a comprehensive AI-assisted intelligent learning platform designed to support K-12 education. The discussions also addressed recent policy changes within the SUV program, current market trends, as well as the challenges and opportunities faced by startups in the Canadian education technology sector. To ensure smooth progress, MYTEC committed to providing timely market development reports and product validation reports. These will be reviewed by PacTech before submission to Canadian immigration authorities as part of the SUV program compliance process. Both MYTEC and PacTech reaffirmed their confidence in the project’s future and pledged to maintain close communication, ensuring that MYTutor Math AI advances successfully toward becoming a cornerstone of AI-assisted learning solutions in Canada and beyond. About PacTech: PacTech is a Canadian incubator supporting technology-driven startups, providing strategic guidance, resources, and compliance oversight under the Canadian Startup Visa program.',
          expanded: false
        },
        {
          img: '/static/about/6.png',
          date: 'April 5, 2025',
          title: 'A New Milestone for MYTEC\'s SUV Project',
          excerpt: 'Surrey, BC — The arrival of MYTEC CEO Jim Zhang in Canada marks a major milestone for the company\'s Start-Up Visa expansion and localized market development.',
          body: 'Surrey, BC — April 5, 2025 — Mr. Jim Zhang Hong, CEO of MYTEC Education Inc. and UEC Academy Ltd., and Director of HETU Technology Ltd., has officially arrived in Surrey, British Columbia on April 4, 2025. His arrival marks a significant milestone in the strategic expansion of MYTEC\'s Start-Up Visa (SUV) project in Canada. With Mr. Zhang’s presence on the ground, MYTEC’s product development, market research, and promotional efforts will now enter a new phase that is more deeply aligned with local market dynamics and community needs. This move demonstrates the company’s commitment to innovation, localization, and long-term growth in the Canadian education and technology sectors. “This is more than just a relocation,” said Mr. Zhang. “It’s a reaffirmation of our belief in the potential of Canada’s innovation ecosystem and our mission to provide forward-thinking educational and technological solutions tailored to the North American market.” MYTEC Education Inc. has been actively engaged in developing advanced learning platforms and integrated solutions for students and educators worldwide. The SUV project represents a cornerstone of the company’s international strategy, aiming to bridge global resources with local impact. Mr. Zhang\'s relocation also strengthens ties with Canadian partners and opens new doors for collaboration in both the education and technology sectors.',
          expanded: false
        },
        {
          img: '/static/about/7.png',
          date: 'December 9, 2024',
          title: 'MYTEC Advances Canadian Market Entry with Pactech Ventures. Following Successful SUV Program Approval',
          excerpt: 'Vancouver, Canada — After SUV approval, MYTEC and Pactech Ventures aligned on market entry, pilot programs, localization strategy, and operational rollout in Canada.',
          body: 'Vancouver, Canada — December 9, 2024 — MYTEC Education Technology Inc., a newly established startup in AI-powered education technology, has taken a significant step in its Canadian expansion strategy following the recent approval of its Start-Up Visa (SUV) Program application with support from Pactech Ventures, a leading Canadian technology incubator. At a high-level meeting between Ms. Sherry Zhang, the Marketing Director of MYTEC, and Mr. Wader Larson, the Director of Pactech Ventures, the two parties discussed concrete plans for implementing MYTEC\'s operations in Canada, building on the foundation established through the successful SUV application. The meeting produced several significant areas of alignment that will guide MYTEC\'s North American expansion, including strategic market positioning, education partnership pathways, local team building, and cultural-linguistic adaptation. Looking ahead, both sides confirmed plans to finalize operational foundations, initiate school pilot programs, and prepare for broader commercial availability. This collaboration represents more than market entry — it is the beginning of MYTEC\'s contribution to Canada\'s education technology innovation ecosystem.',
          expanded: false
        },
        {
          img: '/static/about/8.png',
          date: 'March 30, 2024',
          title: 'Mytec Education Technology Inc. Unveils AI-Powered K12 Learning Platform MYTutor: An Exclusive Interview with CTO Ms. Gu Jing',
          excerpt: 'Dalian, China — In an interview, CTO Gu Jing introduced MYTutor\'s adaptive AI engine, analytics, gamification, and school-system integration vision for K12 education.',
          body: 'Dalian, China — March 30, 2024 — In the rapidly evolving landscape of education technology, Mytec Education Technology Inc. has been developing MYTutor, an AI-driven personalized learning platform designed to transform K12 education across multiple subjects. In an exclusive interview, CTO Ms. Gu Jing shared the platform\'s development philosophy and technological roadmap. She highlighted four key innovations: adaptive learning powered by AI, real-time analytics with automated grading, gamified learning and competitions, and seamless integration with existing school systems. She also shared MYTEC\'s expansion plan beyond core subjects into STEM, coding, and vocational learning scenarios. The interview reinforced MYTEC\'s long-term vision: delivering data-driven, personalized education that helps every student learn at the right pace and path.',
          expanded: false
        }
      ]
    }
  },
  methods: {
    scrollToSection (id) {
      const el = document.getElementById(id)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    ripple (e) {
      if (!e || !e.clientX) return
      const size = 120
      const x = e.clientX
      const y = e.clientY
      this.rippleStyle = {
        left: `${x}px`,
        top: `${y}px`,
        width: `${size}px`,
        height: `${size}px`,
        marginLeft: `-${size / 2}px`,
        marginTop: `-${size / 2}px`
      }
      this.rippleVisible = true
      this.$nextTick(() => {
        const el = this.$el.querySelector('.ripple-fixed')
        if (el) {
          el.classList.remove('ripple-active')
          void el.offsetWidth
          el.classList.add('ripple-active')
        }
        clearTimeout(this._rippleTimer)
        this._rippleTimer = setTimeout(() => {
          this.rippleVisible = false
        }, 650)
      })
    },
    scrollToLandingContact () {
      this.$router.push('/landing').then(() => {
        this.$nextTick(() => {
          const el = document.getElementById('contact')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      })
    },
    onImgError (e) {
      const el = e && e.target
      if (!el || el.dataset.fallbackUsed) return
      const fallback = el.dataset.fallback
      if (fallback) {
        el.dataset.fallbackUsed = '1'
        el.src = fallback
      } else {
        el.style.display = 'none'
      }
    },
    onTeamImgError (e, member) {
      const el = e && e.target
      if (!el) return
      if (el.dataset.fallbackUsed) {
        if (member) member.avatarError = true
        el.style.display = 'none'
        return
      }
      const fallback = el.dataset.fallback
      if (fallback) {
        el.dataset.fallbackUsed = '1'
        el.src = fallback
      } else {
        el.style.display = 'none'
        if (member) member.avatarError = true
      }
    },
    onSubmit () {
      // 可接后端
      this.$message?.success?.('Message sent! We will contact you soon.') || alert('Message sent! We will contact you soon.')
    }
  }
}
</script>

<style lang="scss" scoped>
/* 与 Landing 一致的设计变量与基础 */
.landing-page {
  --lp-section-pad: 64px;
  --lp-gap: 24px;
  --lp-gap-sm: 12px;
  --lp-gap-lg: 40px;
  --lp-card-pad: 24px;
  --lp-radius: 12px;
  --lp-max: 1200px;
  --lp-margin-vw: 6%;
  --lp-label-weight: 700;
  font-family: 'Ubuntu', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f9fafc;
  color: #1e293b;
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
}

@keyframes ripple-anim {
  to { transform: scale(2.5); opacity: 0; }
}

.ripple { position: absolute; border-radius: 50%; background: rgba(255,255,255,0.6); pointer-events: none; }
.ripple-fixed { position: fixed; z-index: 9999; }
.ripple-active { animation: ripple-anim 0.6s ease-out forwards; }

/* Header：与 Landing 一致 */
.landing-header {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 12px 0;
  box-shadow: none;
  width: 100%;
  box-sizing: border-box;
}
.header-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.header-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
}
.header-logo-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: block;
}
.header-logo-text {
  font-size: 1.5rem;
  letter-spacing: -0.01em;
}
.header-logo-bold {
  font-weight: 700;
  color: #020617;
}
.header-logo-light {
  font-weight: 700;
  color: #020617;
}
.header-brand:hover .header-logo-bold { color: #020617; }
.header-brand:hover .header-logo-light { color: #020617; }
.header-nav {
  display: flex;
  align-items: center;
  gap: 0;
}
.header-link {
  font-size: 16px;
  font-weight: 400;
  color: #4b5563;
  text-decoration: none;
  transition: color 0.2s;
  padding: 0 14px;
}
.header-link:hover {
  color: #111827;
}
.header-sep {
  display: none;
}
.header-cta {
  display: flex;
  align-items: center;
}
.btn-cta-nav {
  padding: 10px 22px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  background: linear-gradient(90deg, #f97316 0%, #ef4444 100%);
  transition: opacity 0.2s, transform 0.2s;
  position: relative;
  overflow: hidden;
  height: 48px;
}
.btn-cta-nav:hover {
  opacity: 0.95;
  transform: translateY(-1px);
}
@media (max-width: 768px) {
  .header-nav { display: none; }
}

/* Hero About：带背景图，与 Landing 一致 */
.hero-about {
  text-align: center;
  width: 100%;
  padding: var(--lp-section-pad) var(--lp-margin-vw);
  box-sizing: border-box;
  position: relative;
  min-height: 320px;
  overflow: hidden;
}
.hero-about-with-bg .hero-bg-wrap {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;
  border-radius: 0 0 var(--lp-radius) var(--lp-radius);
}
.hero-about-with-bg .hero-bg-wrap::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(249, 250, 252, 0.85) 0%, rgba(249, 250, 252, 0.95) 100%);
}
.hero-about-with-bg .hero-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hero-about-with-bg .hero-pretitle,
.hero-about-with-bg .hero-title,
.hero-about-with-bg .hero-subtitle,
.hero-about-with-bg .hero-cta {
  position: relative;
  z-index: 1;
}
.hero-pretitle {
  font-size: 0.9375rem;
  color: #6f767e;
  margin: 0 0 var(--lp-gap-sm) 0;
}
.hero-title {
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  font-weight: 700;
  color: #232323;
  margin: 0 0 var(--lp-gap-sm) 0;
  line-height: 1.2;
}
.hero-subtitle {
  font-size: clamp(1rem, 2vw, 1.125rem);
  color: #6f767e;
  margin: 0 0 var(--lp-gap) 0;
  line-height: 1.5;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}
.hero-cta { display: flex; gap: var(--lp-gap); justify-content: center; flex-wrap: wrap; margin-bottom: 0; }
.btn-hero-primary {
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 6px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
  transition: opacity 0.2s, transform 0.2s;
  position: relative;
  overflow: hidden;
}
.btn-hero-primary:hover { opacity: 0.95; transform: translateY(-1px); }
.hero-cta-arrow { margin-left: 6px; }

/* Section 通用 */
.section {
  width: 100%;
  padding: var(--lp-section-pad) var(--lp-margin-vw);
  box-sizing: border-box;
  text-align: center;
}
.section-alt { background: #fff; }
.section-badge-pill {
  display: inline-block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #9133ae;
  background: #f7edfc;
  padding: 6px 14px;
  border-radius: 999px;
  margin-bottom: var(--lp-gap-sm);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.section-title {
  font-size: clamp(1.375rem, 2.5vw, 1.75rem);
  font-weight: var(--lp-label-weight);
  color: #232323;
  margin-bottom: var(--lp-gap-sm);
  line-height: 1.3;
}
.section-desc {
  font-size: 0.9375rem;
  color: #6f767e;
  line-height: 1.6;
  margin-bottom: var(--lp-gap);
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}
.section-desc-wide { max-width: 720px; }

.about-block {
  max-width: 720px;
  margin: 0 auto;
  text-align: left;
}
.about-mission p {
  font-size: 1rem;
  color: #475569;
  line-height: 1.7;
  margin-bottom: var(--lp-gap);
}
.about-mission p:last-child { margin-bottom: 0; }

.feature-link {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #7c3aed;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s;
}
.feature-link:hover { color: #6d28d9; text-decoration: underline; }
.feature-link-btn {
  padding: 12px 24px;
  border-radius: 8px;
  background: linear-gradient(90deg, #8b5cf6 0%, #3b82f6 100%);
  color: #fff !important;
}
.feature-link-btn:hover { text-decoration: none; opacity: 0.95; }

.about-product-img-wrap {
  max-width: 560px;
  margin: 0 auto var(--lp-gap);
  border-radius: var(--lp-radius);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.about-product-img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

/* News */
.news-list {
  display: grid;
  gap: var(--lp-gap);
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
}
.news-card {
  background: #fff;
  padding: var(--lp-card-pad);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.flex{
  display: flex;
  .left{
    max-width: 200px;
    display: flex;
    align-items: center;
    margin-right: 36px;
    img {
      width: 200px;
      height: auto;
      border-radius: 8px;
      object-fit: cover;
      display: block;
      margin: auto 0;
    }
  }
}
.news-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
}
.news-date {
  font-size: 0.8125rem;
  color: #6f767e;
  margin-bottom: 6px;
  display: block;
}
.news-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #232323;
  margin: 0 0 var(--lp-gap-sm) 0;
  line-height: 1.3;
}
.news-excerpt {
  font-size: 0.9375rem;
  color: #6f767e;
  line-height: 1.6;
  margin: 0;
}
.news-more {
  background: none;
  border: none;
  color: #7c3aed;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-top: 8px;
  font-size: 0.9375rem;
}

/* Team */
.team-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--lp-gap);
  max-width: 900px;
  margin: 0 auto;
}
.team-card {
  background: #fff;
  padding: var(--lp-card-pad);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  text-align: center;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.team-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
}
.team-avatar-wrap {
  width: 64px;
  height: 64px;
  margin: 0 auto var(--lp-gap-sm) auto;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%);
}
.team-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.team-avatar {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  color: #fff;
  font-weight: 700;
  font-size: 1.25rem;
  display: none;
  align-items: center;
  justify-content: center;
}
.team-avatar-visible {
  display: flex;
}
.team-name {
  font-size: 1rem;
  font-weight: 700;
  color: #232323;
  margin: 0 0 4px 0;
}
.team-role {
  font-size: 0.8125rem;
  color: #7c3aed;
  font-weight: 600;
  margin: 0 0 4px 0;
}
.team-title {
  font-size: 0.875rem;
  color: #6f767e;
  margin: 0;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .team-grid { grid-template-columns: 1fr; }
}

.section-get-started {
  background: linear-gradient(to right, #f5f3ff 0%, #fafafa 50%, #fff 100%);
  width: 100%;
  padding: 3rem 6rem;
  box-sizing: border-box;
  .get-started{
    max-width: 80rem;
    margin-left: auto;
    margin-right: auto;
  }
}
.get-started-title {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: var(--lp-label-weight);
  color: var(--lp-text-color);
  text-align: center;
  margin: 0 0 var(--lp-gap-sm) 0;
}
.get-started-desc {
  font-size: 1rem;
  color: var(--lp-text-muted);
  text-align: center;
  margin: 0 auto var(--lp-gap) auto;
  max-width: 560px;
  line-height: 1.5;
}
.get-started-grid {
  max-width: 100%;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: calc(2 * var(--lp-gap));
  align-items: start;
}
.get-started-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--lp-gap-sm);
}
.form-label {
  font-size: var(--lp-label-size);
  font-weight: var(--lp-label-weight);
  color: var(--lp-text-color);
  text-align: left;
}
.get-started-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: #fff;
  font-family: inherit;
  transition: border-color 0.2s;
}
.get-started-input::placeholder {
  color: #999;
}
.get-started-input:focus {
  outline: none;
  border-color: #8b5cf6;
}
.get-started-select {
  cursor: pointer;
  appearance: auto;
}
.get-started-textarea {
  resize: vertical;
  min-height: 100px;
}
.get-started-char {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
}
.btn-get-started {
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(to right, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%);
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  margin-top: 4px;
}
.btn-get-started:hover {
  opacity: 0.95;
  transform: translateY(-1px);
}

/* 右侧联系信息块：与表单统一间距 */
.get-started-info {
  display: flex;
  flex-direction: column;
  gap: 48px;
  margin-top: 48px;
}
.info-block {
  display: flex;
  align-items: flex-start;
  gap: var(--lp-gap-sm);
}
.info-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid;
  display: flex;
  align-items: center;
  justify-content: center;
}
.info-icon svg {
  width: 22px;
  height: 22px;
}
.info-icon-purple {
  border-color: #8b5cf6;
  color: #8b5cf6;
}
.info-icon-blue {
  border-color: #3b82f6;
  color: #3b82f6;
}
.info-icon-teal {
  border-color: #14b8a6;
  color: #14b8a6;
}
.info-content {
  flex: 1;
  min-width: 0;
}
.info-heading {
  font-size: var(--lp-label-size);
  font-weight: var(--lp-label-weight);
  color: var(--lp-text-color);
  margin: 0 0 var(--lp-gap-sm) 0;
  text-align: left;
}
.info-text {
  font-size: 0.9375rem;
  color: var(--lp-text-muted);
  margin: 0;
  line-height: 1.5;
  text-align: left;
}
.info-sub {
  font-size: 0.8125rem;
  color: #999;
  margin: var(--lp-gap-sm) 0 0 0;
  text-align: left;
}
.info-link {
  color: #666;
  text-decoration: none;
  text-align: left;
}
.info-link:hover {
  color: #333;
}
.info-block-follow {
  flex-direction: column;
  gap: var(--lp-gap-sm);
}
.info-block-follow .info-heading {
  margin-bottom: 0;
}
.info-social {
  display: flex;
  gap: var(--lp-gap-sm);
}
.info-social-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #333;
  background: #fff;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}
.info-social-icon:hover {
  background: #333;
  color: #fff;
}
.info-social-icon svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 900px) {
  .get-started-grid {
    grid-template-columns: 1fr;
    gap: calc(var(--lp-gap) + var(--lp-gap-sm));
  }
}

/* 页脚：左右边距 6% 视口 */
.landing-footer {
  background: #101010;
  color: #fff;
  width: 100%;
  padding: calc(var(--lp-section-pad) * 0.875) 0 0;
  margin-top: 0;
  box-sizing: border-box;
}
.footer-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  width: 100%;
}
.footer-main {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--lp-gap-lg);
  padding-bottom: var(--lp-gap);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  align-items: start;
}
.footer-col-brand {
  max-width: 320px;
}
.footer-logo {
  font-size: 1.5rem;
  font-weight: var(--lp-label-weight);
  color: #fff;
  margin-bottom: var(--lp-gap-sm);
  letter-spacing: -0.02em;
}
.footer-logo-accent {
  font-weight: var(--lp-label-weight);
}
.footer-desc {
  font-size: 0.9375rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 var(--lp-gap) 0;
}
.footer-social {
  display: flex;
  gap: var(--lp-gap-sm);
  align-items: center;
}
.footer-social-icon {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: background 0.2s, border-color 0.2s;
}
.footer-social-icon:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #fff;
}
.footer-social-icon svg {
  width: 18px;
  height: 18px;
}
.footer-col-contact .footer-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 var(--lp-gap-sm) 0;
  text-transform: uppercase;
}
.footer-col-contact .footer-label:not(:first-child) {
  margin-top: var(--lp-gap);
}
.footer-contact-line {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 4px 0;
  line-height: 1.5;
}
.footer-link {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}
.footer-link:hover {
  color: #fff;
}
.footer-col-img {
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
}
.footer-img-wrap {
  width: 100%;
  max-width: 280px;
  border-radius: var(--lp-radius);
  overflow: hidden;
  background: #1a1a1a;
}
.footer-img {
  width: 100%;
  height: 100%;
  min-height: 200px;
  object-fit: cover;
  display: block;
}
.footer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--lp-gap-sm);
  padding: var(--lp-gap) 0;
}
.footer-copy {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.6);
}
.footer-legal {
  display: flex;
  align-items: center;
  gap: var(--lp-gap);
}
.footer-legal-link {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.2s;
}
.footer-legal-link:hover {
  color: rgba(255, 255, 255, 0.9);
}
@media (max-width: 900px) {
  .footer-main {
    grid-template-columns: 1fr 1fr;
  }
  .footer-col-img {
    grid-column: 1 / -1;
    justify-content: center;
  }
  .footer-img-wrap {
    max-width: 100%;
  }
}
@media (max-width: 600px) {
  .footer-main {
    grid-template-columns: 1fr;
    gap: var(--lp-gap);
  }
  .footer-col-brand {
    max-width: none;
  }
  .footer-bar {
    flex-direction: column;
    text-align: center;
  }
}
</style>
